/**
 * RELIRE une fiche, une capsule, une erreur et son remède — les trois
 * sous-pages de « Programme » (chantier « manuel vivant », Φ-MV-2, MV-CNT-06,
 * MV-SAV-04).
 *
 *   /programme/:code/fiches/:fiche[?ancre=]   la fiche, telle que l'élève la lit
 *   /programme/:code/capsules/:capsule        la capsule, ses sous-titres, sa provenance
 *   /programme/:code/erreurs/:erreur          l'erreur, et le remède qu'elle pointe
 *
 * Une adresse chacune : un relecteur envoie « regarde ce piège » par un lien,
 * et un retour arrière ramène à la liste (même règle que l'administration).
 *
 * ─── Ce qu'on y fait, et ce qu'on n'y fait pas ──────────────────────────────
 *
 * On LIT, puis on dit « relu, rien à changer », ou l'on sort le contenu du
 * service. On ne CORRIGE pas ici : une fiche se corrige dans son dossier de
 * contenu et revient par l'import, en version neuve ; une capsule se corrige
 * dans son script, et la chaîne vidéo la refait. Récrire en place un contenu
 * publié ferait mentir les consultations déjà faites.
 *
 * ─── Un contenu se lit dans son chapitre ────────────────────────────────────
 *
 * Chaque page charge aussi le chapitre : c'est lui qui numérote les notions
 * (« 3.2 ») et qui porte le catalogue d'erreurs — donc la réponse à « ce piège
 * répond à quelle erreur ? », et « cette erreur a-t-elle son remède en
 * service ? ».
 *
 * Aucun code interne à l'écran : les codes stables restent dans les adresses.
 */
import {
  dureeEnClair,
  repereCourant,
  type CapsuleRelectureResponse,
  type ChapitreProgrammeResponse,
  type ContentStatus,
  type ErreurRelectureDto,
  type FicheRelectureResponse,
  type SavoirChapitreResponse,
} from '@silex/shared';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { ActionsSavoir } from '../components/ActionsSavoir';
import { Badge } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { FicheCoursWeb } from '../components/fiche/FicheCoursWeb';
import { TexteMaths } from '../components/TexteMaths';
import { manuelVivantActif } from '../lib/manuel-vivant';
import {
  adresses,
  etatDuRemede,
  etatRelecture,
  libelleCategorie,
  nommerNotions,
  piegesDeLaFiche,
  statutService,
  voisinsDeRelecture,
} from '../lib/savoir-format';
import { formatDate } from '../lib/tuteur-format';
import { useAsyncRechargeable, type Resource } from '../lib/useAsync';

/* ─────────────────────────── le chapitre autour ─────────────────────────── */

interface Autour {
  readonly chapitre: ChapitreProgrammeResponse;
  readonly savoir: SavoirChapitreResponse;
}

/** Le chapitre et sa base de savoir, chargés ensemble avec la ressource relue. */
function useRelecture<T>(
  code: string | undefined,
  lire: (() => Promise<T>) | null,
): {
  readonly resource: Resource<{ readonly autour: Autour; readonly donnees: T }>;
  readonly recharger: () => void;
} {
  const loader = useMemo(
    () =>
      code && lire
        ? async () => {
            const [chapitre, savoir, donnees] = await Promise.all([
              api.programmeChapitre(code),
              api.savoirChapitre(code),
              lire(),
            ]);
            return { autour: { chapitre, savoir }, donnees };
          }
        : null,
    [code, lire],
  );
  return useAsyncRechargeable(loader);
}

/* ─────────────────────── précédente · suivante (L4) ─────────────────────── */

type SorteRelue = 'fiches' | 'capsules' | 'erreurs';

/** Les contenus d'une sorte, dans l'ordre du chapitre, et s'ils sont relus. */
function listeDeLaSorte(savoir: SavoirChapitreResponse, sorte: SorteRelue) {
  if (sorte === 'erreurs') {
    return savoir.erreurs.map((e) => ({ code: e.code, relu: e.statutRelecture !== 'genere' }));
  }
  return savoir[sorte].map((x) => ({
    code: x.code,
    relu: x.relecture.statutRelecture !== 'genere',
  }));
}

const ADRESSE_DE: Readonly<Record<SorteRelue, (chapitre: string, code: string) => string>> = {
  fiches: (ch, c) => adresses.fiche(ch, c),
  capsules: adresses.capsule,
  erreurs: adresses.erreur,
};
const NOM_DE: Readonly<Record<SorteRelue, readonly [string, string]>> = {
  fiches: ['fiche', 'Fiche'],
  capsules: ['capsule', 'Capsule'],
  erreurs: ['erreur', 'Erreur'],
};

/**
 * L4 — « suivante » : la relecture enchaîne les contenus d'une sorte sans
 * repasser par le chapitre. « Relu » mène au prochain NON relu ; quand il n'y
 * en a plus, la page se recharge et le dit.
 */
function useSuivante(autour: Autour, sorte: SorteRelue, code: string) {
  const navigate = useNavigate();
  const chapitre = autour.chapitre.chapitre.code;
  const voisins = voisinsDeRelecture(listeDeLaSorte(autour.savoir, sorte), code);
  const aller = (c: string): void => navigate(ADRESSE_DE[sorte](chapitre, c));
  return {
    voisins,
    /** Après « Relu » : le prochain non relu, ou rester (et recharger). */
    apresRelu: (recharger: () => void) => () =>
      voisins?.prochainARelire ? aller(voisins.prochainARelire) : recharger(),
    navigation:
      voisins === null ? null : (
        <nav
          aria-label="Contenus du chapitre"
          data-testid="relecture-suivante"
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            margin: '8px 0 14px',
          }}
        >
          {voisins.precedent ? (
            <Link
              className="btn btn--ghost btn--sm"
              to={ADRESSE_DE[sorte](chapitre, voisins.precedent)}
            >
              ← Précédente
            </Link>
          ) : null}
          <span className="note-line">
            {NOM_DE[sorte][1]} {voisins.position} sur {voisins.total}
          </span>
          {voisins.suivant ? (
            <Link
              className="btn btn--ghost btn--sm"
              to={ADRESSE_DE[sorte](chapitre, voisins.suivant)}
              data-testid="relecture-suivante-lien"
            >
              Suivante →
            </Link>
          ) : null}
          {voisins.prochainARelire === null ? (
            <span className="note-line">
              Toutes les {NOM_DE[sorte][0]}s du chapitre sont relues.
            </span>
          ) : null}
        </nav>
      ),
  };
}

function Cadre({
  chapitre,
  children,
}: {
  readonly chapitre: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <div className="content__inner">
      <p className="note-line fil-retour">
        <Link to={chapitre ? adresses.chapitre(chapitre) : '/programme'}>← Retour au chapitre</Link>
      </p>
      {children}
    </div>
  );
}

function Versions({
  versions,
  courante,
}: {
  readonly versions: readonly { readonly version: number; readonly statut: ContentStatus }[];
  readonly courante: number;
}) {
  return (
    <section className="card relecture-bloc" data-testid="relecture-versions">
      <h3>Versions</h3>
      <ul className="relecture-liste">
        {versions.map((v) => (
          <li key={v.version}>
            <span>
              Version {v.version}
              {v.version === courante ? ' — celle-ci' : ''}
            </span>
            <span className="relecture-liste__etat">{statutService(v.statut).label}</span>
          </li>
        ))}
      </ul>
      <p className="note-line">
        Une version publiée ne se récrit pas : une correction arrive par l’import, en version neuve.
      </p>
    </section>
  );
}

/* ─────────────────────────────── une fiche ─────────────────────────────── */

export function FicheRelecturePage() {
  const { code, fiche } = useParams<{ code: string; fiche: string }>();
  const actif = manuelVivantActif();
  const lire = useMemo(
    () => (actif && fiche ? () => api.ficheRelecture(fiche) : null),
    [actif, fiche],
  );
  const { resource, recharger } = useRelecture(code, lire);

  if (!actif) return <Navigate to="/" replace />;

  return (
    <Cadre chapitre={code}>
      {resource.status === 'loading' ? <Chargement quoi="la fiche" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}
      {resource.status === 'ready' ? (
        <Fiche
          autour={resource.data.autour}
          donnees={resource.data.donnees}
          recharger={recharger}
        />
      ) : null}
    </Cadre>
  );
}

function Fiche({
  autour,
  donnees,
  recharger,
}: {
  readonly autour: Autour;
  readonly donnees: FicheRelectureResponse;
  readonly recharger: () => void;
}) {
  const { chapitre: c, notions: notionsDuChapitre } = autour.chapitre;
  const { fiche, ancres, notions, relecture, versions } = donnees;
  const [params, setParams] = useSearchParams();
  const ancre = params.get('ancre') ?? undefined;
  const viser = (a: string): void => setParams({ ancre: a }, { replace: true });

  const service = statutService(relecture.statut);
  const etat = etatRelecture(relecture, 'fiches');
  const pieges = piegesDeLaFiche(ancres, autour.savoir.erreurs);
  const suivante = useSuivante(autour, 'fiches', fiche.code);

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">{c.titre} · fiche, telle que l’élève la lit</p>
          <h1>
            <TexteMaths texte={fiche.titre} />
          </h1>
          <p className="page-sub">
            {fiche.nature === 'revision' ? 'Fiche de révision · ' : ''}
            {nommerNotions(notions, c.rang, notionsDuChapitre.length)} · version {fiche.version}
          </p>
        </div>
        <div className="relecture-etats">
          <Badge tone={service.tone} label={service.label} />
          <Badge tone={etat.tone} label={etat.label} />
        </div>
      </div>

      <ActionsSavoir
        cible="fiches"
        code={fiche.code}
        statut={relecture.statut}
        relu={etat.relu}
        surChangement={recharger}
        surRelu={suivante.apresRelu(recharger)}
      />
      {suivante.navigation}

      <div className="relecture-page" data-testid="relecture-fiche">
        <div className="relecture-page__feuille">
          <FicheCoursWeb
            fiche={fiche}
            ancre={ancre}
            surtitre={`Fiche ${fiche.rang}`}
            pourRelecture
          />
        </div>

        <aside className="relecture-page__marge">
          <section className="card relecture-bloc" data-testid="relecture-ancres">
            <h3>Dans cette fiche</h3>
            <ul className="relecture-liste relecture-liste--liens">
              {ancres.map((a) => (
                <li key={a.ancre}>
                  <button
                    type="button"
                    className={`relecture-lien${a.erreur ? ' relecture-lien--piege' : ''}`}
                    aria-current={ancre === a.ancre ? 'true' : undefined}
                    onClick={() => viser(a.ancre)}
                  >
                    <TexteMaths texte={a.titre} />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {pieges.length > 0 ? (
            <section className="card relecture-bloc" data-testid="relecture-pieges">
              <h3>Chaque piège, et l’erreur à laquelle il répond</h3>
              <ul className="relecture-liste relecture-liste--blocs">
                {pieges.map((p) => (
                  <li key={p.ancre}>
                    <button
                      type="button"
                      className="relecture-lien relecture-lien--piege"
                      onClick={() => viser(p.ancre)}
                    >
                      {p.titre}
                    </button>
                    {p.erreur ? (
                      <span className="relecture-liste__sous">
                        répond à{' '}
                        <Link to={adresses.erreur(c.code, p.erreur.code)}>
                          « {p.erreur.titre} »
                        </Link>
                      </span>
                    ) : (
                      <span className="relecture-liste__sous relecture-liste__sous--alerte">
                        ne répond à aucune erreur du catalogue de ce chapitre
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <Versions versions={versions} courante={fiche.version} />
        </aside>
      </div>
    </>
  );
}

/* ─────────────────────────────── une capsule ─────────────────────────────── */

export function CapsuleRelecturePage() {
  const { code, capsule } = useParams<{ code: string; capsule: string }>();
  const actif = manuelVivantActif();
  const lire = useMemo(
    () => (actif && capsule ? () => api.capsuleRelecture(capsule) : null),
    [actif, capsule],
  );
  const { resource, recharger } = useRelecture(code, lire);

  if (!actif) return <Navigate to="/" replace />;

  return (
    <Cadre chapitre={code}>
      {resource.status === 'loading' ? <Chargement quoi="la capsule" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}
      {resource.status === 'ready' ? (
        <Capsule
          autour={resource.data.autour}
          donnees={resource.data.donnees}
          recharger={recharger}
        />
      ) : null}
    </Cadre>
  );
}

function Capsule({
  autour,
  donnees,
  recharger,
}: {
  readonly autour: Autour;
  readonly donnees: CapsuleRelectureResponse;
  readonly recharger: () => void;
}) {
  const { chapitre: c, notions: notionsDuChapitre } = autour.chapitre;
  const { capsule, notions, erreursVisees, source, relecture, versions } = donnees;
  const [positionMs, setPositionMs] = useState(0);
  const [sousTitres, setSousTitres] = useState(true);
  const [videoEnPanne, setVideoEnPanne] = useState(false);

  const service = statutService(relecture.statut);
  const etat = etatRelecture(relecture, 'capsules');
  const suivante = useSuivante(autour, 'capsules', capsule.code);
  /* T40 : une capsule À VENIR (mise de côté) n'a ni vidéo, ni répliques, ni transcription. */
  const aVenir = capsule.video === null;
  const replique = capsule.video === null ? null : repereCourant(capsule.reperes, positionMs);
  const provenance: (readonly [string, string])[] = [];
  if (source?.script) provenance.push(['Script', source.script]);
  if (source?.voix) provenance.push(['Voix', source.voix]);
  if (source?.gabarit) provenance.push(['Gabarit de rendu', source.gabarit]);
  if (source?.produitLe) provenance.push(['Produite le', formatDate(source.produitLe)]);

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">
            {c.titre} · capsule · {aVenir ? 'à venir' : dureeEnClair(capsule.dureeSec)}
          </p>
          <h1>{capsule.titre}</h1>
          <p className="page-sub">
            {nommerNotions(notions, c.rang, notionsDuChapitre.length)} · version {capsule.version}
          </p>
        </div>
        <div className="relecture-etats">
          {aVenir ? <Badge tone="en-cours" label="à venir" /> : null}
          <Badge tone={service.tone} label={service.label} />
          <Badge tone={etat.tone} label={etat.label} />
        </div>
      </div>

      <ActionsSavoir
        cible="capsules"
        code={capsule.code}
        statut={relecture.statut}
        relu={etat.relu}
        surChangement={recharger}
        surRelu={suivante.apresRelu(recharger)}
      />
      {suivante.navigation}

      <div className="relecture-page" data-testid="relecture-capsule">
        <div className="relecture-page__feuille">
          {capsule.video === null ? (
            <section className="card relecture-bloc" data-testid="capsule-a-venir">
              <h3>À venir</h3>
              <p>
                Cette capsule a été mise de côté : elle n’a pas encore de vidéo. L’élève la voit
                dans la liste, lit « Cette capsule arrive. », et la fiche de la notion lui reste
                proposée. Elle recevra ses médias à la bonification, sous le même code.
              </p>
            </section>
          ) : (
            <div className="capsule-lecteur">
              {videoEnPanne ? (
                <div className="capsule-lecteur__panne" role="alert">
                  {capsule.affiche ? <img src={capsule.affiche} alt="" /> : null}
                  <p>
                    La vidéo de cette capsule n’a pas pu être chargée. Sa transcription est juste en
                    dessous.
                  </p>
                </div>
              ) : (
                /* Les sous-titres sont AUSSI écrits sous l'image, à partir des
                 mêmes répliques que l'application : c'est ce rendu-là que
                 l'élève lit, et qu'on relit. La piste du lecteur reste offerte. */
                <video
                  className="capsule-lecteur__video"
                  controls
                  preload="metadata"
                  poster={capsule.affiche ?? undefined}
                  src={capsule.video}
                  onTimeUpdate={(e) =>
                    setPositionMs(Math.round(e.currentTarget.currentTime * 1000))
                  }
                  onError={() => setVideoEnPanne(true)}
                  data-testid="capsule-video"
                >
                  {capsule.sousTitres ? (
                    <track
                      kind="subtitles"
                      srcLang="fr"
                      label="Français"
                      src={capsule.sousTitres}
                    />
                  ) : null}
                </video>
              )}
              <div className="capsule-lecteur__sous">
                <p
                  className="capsule-lecteur__replique"
                  aria-live="off"
                  data-testid="capsule-replique"
                >
                  {sousTitres && replique ? replique.texte : ' '}
                </p>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  aria-pressed={sousTitres}
                  onClick={() => setSousTitres((v) => !v)}
                >
                  Sous-titres
                </button>
              </div>
            </div>
          )}

          <section className="card relecture-bloc">
            <h3>L’idée</h3>
            <p>{capsule.idee}</p>
          </section>

          {capsule.video === null ? null : (
            <section className="card relecture-bloc" data-testid="capsule-transcription">
              <h3>Transcription</h3>
              {capsule.transcription.split('\n').map((paragraphe, i) => (
                <p key={i}>{paragraphe}</p>
              ))}
            </section>
          )}
        </div>

        <aside className="relecture-page__marge">
          <section className="card relecture-bloc" data-testid="capsule-erreurs">
            <h3>Erreurs visées</h3>
            {erreursVisees.length === 0 ? (
              <p className="note-line">Cette capsule ne vise aucune erreur du catalogue.</p>
            ) : (
              <ul className="relecture-liste relecture-liste--blocs">
                {erreursVisees.map((e) => (
                  <li key={e.code}>
                    <Link to={adresses.erreur(c.code, e.code)}>{e.titre}</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card relecture-bloc">
            <h3>Exercice de suite</h3>
            <p className="note-line">
              La carte « À toi de jouer » sera reliée à un exercice quand la banque du chapitre sera
              importée. Rien à relire ici pour l’instant.
            </p>
          </section>

          <section className="card relecture-bloc" data-testid="capsule-provenance">
            <h3>Provenance</h3>
            {provenance.length === 0 ? (
              <p className="note-line">Aucune provenance n’a été importée avec cette capsule.</p>
            ) : (
              <ul className="relecture-liste">
                {provenance.map(([nom, valeur]) => (
                  <li key={nom}>
                    <span>{nom}</span>
                    <span className="relecture-liste__etat">{valeur}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="note-line">
              Corriger une capsule, c’est corriger son script et relancer la chaîne vidéo.
            </p>
          </section>

          <Versions versions={versions} courante={capsule.version} />
        </aside>
      </div>
    </>
  );
}

/* ─────────────────────────────── une erreur ─────────────────────────────── */

export function ErreurRelecturePage() {
  const { code, erreur } = useParams<{ code: string; erreur: string }>();
  const actif = manuelVivantActif();
  /* Une erreur n'a pas de lecture à elle : elle est dans le catalogue du chapitre. */
  const lire = useMemo(() => (actif ? () => Promise.resolve(null) : null), [actif]);
  const { resource, recharger } = useRelecture(code, lire);

  if (!actif) return <Navigate to="/" replace />;

  const trouvee =
    resource.status === 'ready'
      ? resource.data.autour.savoir.erreurs.find((e) => e.code === erreur)
      : undefined;

  return (
    <Cadre chapitre={code}>
      {resource.status === 'loading' ? <Chargement quoi="l’erreur et son remède" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}
      {resource.status === 'ready' && trouvee === undefined ? (
        <EmptyState title="Erreur introuvable">
          Cette erreur n’est pas au catalogue de ce chapitre.
        </EmptyState>
      ) : null}
      {resource.status === 'ready' && trouvee !== undefined ? (
        <Erreur autour={resource.data.autour} erreur={trouvee} recharger={recharger} />
      ) : null}
    </Cadre>
  );
}

function Champ({ titre, children }: { readonly titre: string; readonly children: ReactNode }) {
  return (
    <div className="relecture-champ">
      <h4>{titre}</h4>
      <div>{children}</div>
    </div>
  );
}

function Erreur({
  autour,
  erreur: e,
  recharger,
}: {
  readonly autour: Autour;
  readonly erreur: ErreurRelectureDto;
  readonly recharger: () => void;
}) {
  const { chapitre: c, notions: notionsDuChapitre } = autour.chapitre;
  const remede = etatDuRemede(e);
  const etat = etatRelecture(e, 'erreurs');
  const suivante = useSuivante(autour, 'erreurs', e.code);

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">
            {c.titre} · erreur · {libelleCategorie(e.categorie).toLowerCase()}
          </p>
          <h1>{e.titre}</h1>
          <p className="page-sub">{nommerNotions([e.notion], c.rang, notionsDuChapitre.length)}</p>
        </div>
        <div className="relecture-etats">
          <Badge tone={remede.tone} label={remede.label} />
          <Badge tone={etat.tone} label={etat.label} />
        </div>
      </div>

      <ActionsSavoir
        cible="erreurs"
        code={e.code}
        relu={etat.relu}
        surChangement={recharger}
        surRelu={suivante.apresRelu(recharger)}
      />
      {suivante.navigation}

      <div className="relecture-page" data-testid="relecture-erreur">
        <div className="relecture-page__feuille">
          <section className="card relecture-bloc">
            <h3>Ce que l’élève lit</h3>
            <Champ titre="Une écriture typique">
              {e.exemple ? <TexteMaths texte={e.exemple} /> : 'Aucun exemple rédigé.'}
            </Champ>
            <Champ titre="Le message à l’élève">
              {e.messageEleve ? (
                <TexteMaths texte={e.messageEleve} />
              ) : (
                'Aucun message rédigé : cette erreur n’est montrée à aucun élève.'
              )}
            </Champ>
          </section>

          <section className="card relecture-bloc">
            <h3>Ce que le tuteur lit</h3>
            <Champ titre="La conception sous-jacente">
              <TexteMaths texte={e.lectureTuteur} />
            </Champ>
            <Champ titre="Ce qu’on fait pour la défaire">
              <TexteMaths texte={e.remediation} />
            </Champ>
            <Champ titre="Le prérequis que sa récurrence met en cause">
              {e.prerequisEnCause ? e.prerequisEnCause.titre : 'Aucun.'}
            </Champ>
          </section>
        </div>

        <aside className="relecture-page__marge">
          <section className="card relecture-bloc" data-testid="erreur-remedes">
            <h3>Le remède pointé</h3>
            {e.remedes.length === 0 ? (
              <p className="note-line">Aucune fiche ni capsule ne répond à cette erreur.</p>
            ) : (
              <ul className="relecture-liste relecture-liste--blocs">
                {e.remedes.map((r, i) => (
                  <li key={i}>
                    {r.fiche ? (
                      <span className="relecture-remede">
                        <span className="relecture-remede__nature">Fiche</span>
                        <span>
                          « {r.fiche.ficheTitre} », au piège « {r.fiche.titre} »
                        </span>
                        <span className="relecture-liste__sous">
                          {statutService(r.fiche.ficheStatut).label} · version{' '}
                          {r.fiche.ficheVersion}
                        </span>
                        <Link
                          className="btn btn--ghost btn--sm"
                          to={adresses.fiche(c.code, r.fiche.ficheCode, r.fiche.ancre)}
                          data-testid="erreur-ouvrir-piege"
                        >
                          Ouvrir le piège
                        </Link>
                      </span>
                    ) : null}
                    {r.capsule ? (
                      <span className="relecture-remede">
                        <span className="relecture-remede__nature">Capsule</span>
                        <span>
                          « {r.capsule.titre} » ·{' '}
                          {r.capsule.etat === 'a_venir'
                            ? 'à venir'
                            : dureeEnClair(r.capsule.dureeSec)}
                        </span>
                        <span className="relecture-liste__sous">
                          {statutService(r.capsule.statut).label} · version {r.capsule.version}
                        </span>
                        <Link
                          className="btn btn--ghost btn--sm"
                          to={adresses.capsule(c.code, r.capsule.code)}
                        >
                          {r.capsule.etat === 'a_venir'
                            ? 'Ouvrir la capsule'
                            : 'Écouter la capsule'}
                        </Link>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {e.aSonAncre ? null : (
              <div className="callout callout--calm" data-testid="erreur-sans-ancre">
                <strong>Aucun piège d’une fiche en service ne répond à cette erreur.</strong> La
                correction la nommerait sans pouvoir envoyer l’élève quelque part ; le chapitre ne
                peut pas être publié dans cet état.
              </div>
            )}
          </section>
        </aside>
      </div>
    </>
  );
}
